import { Helmet } from 'react-helmet-async';
import { constructListingImageUrl, constructGalleryImageUrl } from '../../../util/ConstructImageUrlUtil';
import type { Supplier } from '../../../types/Supplier';
import { formatMoneyAddDecimalPoint } from '../../../util/PaymentUtils';
import { states } from '../../../util/StateUtil';
import { getLatLng } from '../../../util/SupplierUtils';
import { marketPath, nearestMarket } from '../../../seo/markets.mjs';
import { DEFAULT_IMAGE, useClaimHead } from '../../seo/HeadDefaults';
import NotFoundHead from '../../seo/NotFoundHead';

interface LocationProfileSEOProps {
  location: Supplier | null;
  locationId: string;
  /** The yard doesn't exist: keep the page out of search (securspace-ui#3107). */
  notFound?: boolean;
}

const SITE = 'https://secur.space';

const stripHtml = (html: string): string => html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();

// The API sends the full state name ("Georgia"); searchers type "GA", and the
// market pages (/yards/us/ga/savannah) use the abbreviation. Unknown values
// (territories, bad data) fall through unchanged.
export const stateAbbr = (state: string | undefined): string => (state && states.get(state)) || state || '';
const US_STATE_ABBRS: ReadonlySet<string> = new Set(states.values());

/**
 * "Truck parking & container storage · Pooler, GA" — the one geo line the page
 * shows under the H1 (LocationProfileHeader) and appends to its meta
 * description, so the visible text and the snippet always agree.
 */
export const geoLine = (city: string | undefined, state: string | undefined): string =>
  `Truck parking & container storage · ${[city, stateAbbr(state)].filter(Boolean).join(', ')}`;

/**
 * Meta description. Supplier-written copy first (it is what Google shows as the
 * snippet), always followed by the geo line so every snippet names the city
 * and state even when the supplier did not. Cut at a word boundary to fit.
 */
export const getDescription = (location: Supplier, maxLength: number): string => {
  const geo = geoLine(location.city, location.state);
  // Supplier copy, or a generic sentence when there is none — either way it is
  // the part that gets trimmed; the geo line is never cut.
  // Decide on the STRIPPED text: a description that is only markup ("<p></p>")
  // is truthy but empty once stripped, and must take the generic sentence too.
  const own = stripHtml(location.locationDescription || '')
    || `Book secure logistics space at ${location.locationName} on SecurSpace`;
  const budget = maxLength - geo.length - 2; // ". "
  if (budget < 20) return geo.substring(0, maxLength);
  const cut = own.length > budget ? own.substring(0, budget).replace(/\s+\S*$/, '') : own;
  return `${cut.replace(/[,;:.\s]+$/, '')}. ${geo}`;
};

const getImageUrl = (location: Supplier): string => {
  if (location.listingImageFileName) {
    return constructListingImageUrl(location.listingImageFileName);
  }
  if (location.gallery?.length) {
    return constructGalleryImageUrl(location.gallery[0]);
  }
  return '';
};

const getKeywords = (location: Supplier): string => {
  return [
    `truck parking ${location.city}`,
    `container storage ${location.state}`,
    `trailer parking ${location.city} ${location.state}`,
    ...(location.equipmentTypes || []).map(t => `${t} storage ${location.city}`),
    ...(location.features || []).slice(0, 5),
  ].join(', ');
};

/**
 * schema.org type(s) from what the yard accepts. Containers/chassis/reefers are
 * storage; trucks and trailers are parking; a yard taking both is both, which is
 * what Google's LocalBusiness guidance expects for dual-use sites.
 *
 * Storage is SelfStorage (there is no schema.org StorageFacility: validators
 * reject it, #3133). ParkingFacility is a CivicStructure, not a business, so a
 * parking-only yard is also a LocalBusiness: that is what makes priceRange,
 * telephone, parentOrganization and the supplier's subOrganization valid.
 */
export const getSchemaTypes = (equipmentTypes: string[] | undefined): string | string[] => {
  const types = equipmentTypes || [];
  const parking = types.some(t => /truck|trailer/i.test(t));
  const storage = types.some(t => /container|chassis|refrigerated/i.test(t));
  if (parking && storage) return ['SelfStorage', 'ParkingFacility'];
  if (parking) return ['ParkingFacility', 'LocalBusiness'];
  return 'SelfStorage';
};

// Google reads coordinates to 5 decimals (~1 m); the API returns strings.
const coord = (value: string | number | undefined): number | undefined => {
  const n = typeof value === 'number' ? value : parseFloat(value || '');
  return Number.isFinite(n) ? Number(n.toFixed(5)) : undefined;
};

// API amounts are cents (3500 => $35.00), like everywhere else in the app.
// The previous version printed the raw integer, so crawlers were told $3500/day.
const priceRange = (location: Supplier): string | undefined =>
  location.pricePerDay && location.pricePerMonth
    ? `$${formatMoneyAddDecimalPoint(location.pricePerDay)}/day – $${formatMoneyAddDecimalPoint(location.pricePerMonth)}/month`
    : undefined;

export const buildStructuredData = (location: Supplier, locationId: string, imageUrl: string) => {
  const url = `${SITE}/location-profile/${locationId}`;
  return {
    '@context': 'https://schema.org',
    '@type': getSchemaTypes(location.equipmentTypes),
    '@id': url,
    name: location.locationName,
    description: getDescription(location, 300),
    url,
    telephone: location.locationPhoneNumber || location.companyPhoneNumber || undefined,
    address: {
      '@type': 'PostalAddress',
      streetAddress: [location.addressLine1, location.addressLine2].filter(Boolean).join(', ') || undefined,
      addressLocality: location.city,
      addressRegion: stateAbbr(location.state),
      postalCode: location.zip || undefined,
      addressCountry: 'US',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: coord(location.addressLatitude),
      longitude: coord(location.addressLongitude),
    },
    image: imageUrl || undefined,
    priceRange: priceRange(location),
    // Same @id SupplierProfileSEO emits for the supplier page, so crawlers join the two entities.
    parentOrganization: location.supplierProfileUrl
      ? {
        '@type': 'Organization',
        '@id': `${SITE}${location.supplierProfileUrl}#organization`,
        name: location.companyName || undefined,
      }
      : undefined,
    amenityFeature: (location.features || []).map(f => ({
      '@type': 'LocationFeatureSpecification',
      name: f,
      value: true,
    })),
  };
};

/**
 * Home › the nearest market within 50 miles › the yard (securspace-ui#3055). The market
 * crumb links the market page, not the yard's own suburb, so yard pages link into the
 * page Google should rank. With no market in range, the yard's own city takes its place
 * (securspace-ui#3095), so every yard links to the yards around it; Home › the yard
 * when the city or a US state is missing.
 * The JSON-LD and the visible LocationProfileBreadcrumbs both read this trail.
 */
export const breadcrumbTrail = (location: Supplier, locationId: string): Array<{ name: string, path: string }> => {
  const at = getLatLng(location);
  const city = location.city?.trim();
  const state = stateAbbr(location.state);
  const place = (at ? nearestMarket(at) : undefined) ?? (city && US_STATE_ABBRS.has(state) ? { city, state } : undefined);
  return [
    { name: 'Home', path: '/' },
    ...(place ? [{ name: `${place.city}, ${place.state}`, path: marketPath(place) }] : []),
    { name: location.locationName, path: `/location-profile/${locationId}` },
  ];
};

export const buildBreadcrumbs = (location: Supplier, locationId: string) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: breadcrumbTrail(location, locationId).map((crumb, i) => ({
    '@type': 'ListItem', position: i + 1, name: crumb.name, item: `${SITE}${crumb.path}`,
  })),
});

const LocationProfileSEO = ({ location, locationId, notFound = false }: LocationProfileSEOProps) => {
  useClaimHead();
  if (notFound) return <NotFoundHead title="Location not found | SecurSpace" />;
  if (!location) {
    const canonicalUrl = `${SITE}/location-profile/${locationId}`;
    const fallbackTitle = 'SecurSpace | Nationwide Truck Parking & Storage';
    const fallbackDescription = 'Find secure truck parking and container storage across the United States with SecurSpace.';
    return (
      <Helmet>
        <title>{fallbackTitle}</title>
        <meta name="description" content={fallbackDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={fallbackTitle} />
        <meta property="og:description" content={fallbackDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SecurSpace" />
        <meta property="og:image" content={DEFAULT_IMAGE} />
      </Helmet>
    );
  }

  const title = `${location.locationName} — Truck Parking & Storage in ${location.city}, ${stateAbbr(location.state)} | SecurSpace`;
  const description = getDescription(location, 160);
  const canonicalUrl = `${SITE}/location-profile/${locationId}`;
  const imageUrl = getImageUrl(location);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={getKeywords(location)} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl || DEFAULT_IMAGE} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="SecurSpace" />
      <script type="application/ld+json">
        {JSON.stringify(buildStructuredData(location, locationId, imageUrl))}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(buildBreadcrumbs(location, locationId))}
      </script>
    </Helmet>
  );
};

export default LocationProfileSEO;
